type SavedLocation = {
    pathname: string;
    search: string;
};

type ReturnLocation = SavedLocation & {
    state?: unknown;
};

const RETURN_URL_PARAM = 'returnUrl';
const RETURN_PATH_STORAGE_KEY = 'adminAuthReturnPath';
const DEFAULT_RETURN_PATH = '/dashboard';
const AUTH_PATHS = new Set(['/signin', '/set-password']);

export const saveReturnPath = (pathname: string, search = ''): void => {
    const path = `${pathname}${search}`;
    if (isSafeReturnPath(path)) {
        sessionStorage.setItem(RETURN_PATH_STORAGE_KEY, path);
    }
};

export const peekReturnPath = (): string | null => {
    const path = sessionStorage.getItem(RETURN_PATH_STORAGE_KEY);
    if (path && isSafeReturnPath(path)) {
        return path;
    }

    return null;
};

export const resolveReturnPath = (search = ''): string | null => {
    const storedPath = peekReturnPath();
    if (storedPath) {
        sessionStorage.removeItem(RETURN_PATH_STORAGE_KEY);
        return storedPath;
    }

    const returnUrl = new URLSearchParams(search).get(RETURN_URL_PARAM);
    if (returnUrl && isSafeReturnPath(returnUrl)) {
        return returnUrl;
    }

    return null;
};

export const consumeReturnPath = (search = ''): string => {
    return resolveReturnPath(search) ?? DEFAULT_RETURN_PATH;
};

export const splitReturnPath = (path: string): { pathname: string; search: string } => {
    const queryIndex = path.indexOf('?');

    if (queryIndex === -1) {
        return { pathname: path, search: '' };
    }

    return {
        pathname: path.slice(0, queryIndex),
        search: path.slice(queryIndex),
    };
};

export const clearReturnPath = (): void => {
    sessionStorage.removeItem(RETURN_PATH_STORAGE_KEY);
};

export const saveReturnPathFromLocation = (location: ReturnLocation): void => {
    const fromState = (location.state as { from?: SavedLocation } | null | undefined)?.from;
    if (fromState) {
        saveReturnPath(fromState.pathname, fromState.search);
        return;
    }

    const returnUrl = new URLSearchParams(location.search).get(RETURN_URL_PARAM);
    if (returnUrl && isSafeReturnPath(returnUrl)) {
        sessionStorage.setItem(RETURN_PATH_STORAGE_KEY, returnUrl);
    }
};

export const isSafeReturnPath = (path: string): boolean => {
    if (!path || !path.startsWith('/') || path.startsWith('//')) {
        return false;
    }

    if (path.includes('://')) {
        return false;
    }

    const pathname = path.split('?')[0];
    return !AUTH_PATHS.has(pathname);
};
